//@ts-nocheck
import {
  generateSnapThreshold,
  getSnapResultForElement,
} from '@src/utility/elementSnapping';
import { runInAction } from 'mobx';

import type { ElementState } from '@src/renderer/ElementState';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { ElementPlacementOverride } from '@src/stores/TimelineStore';
import type TimelineStore from '@src/stores/TimelineStore';
import type { ElementBoundary } from '@src/utility/elementSnapping';

export class ElementPlacementController {
  private videoCreatorStore: IVideoCreatorStore;
  private elements: TimelineElement[] = [];
  private timelineStore: TimelineStore;

  constructor(private readonly videoStore: IVideoCreatorStore) {
    this.timelineStore = this.videoStore.timelineStore;
    this.videoCreatorStore = videoStore;
    this.elements = (this.videoStore.state?.elements || []).map(element => {
      return new TimelineElement(
        element,
        this.timelineStore.overrideTimelineElementsState[element.source.id],
      );
    });
  }

  public setElementsTimelinePosition(
    elements: ElementState[],
    timeOffset: number,
    yOffset: number,
    targetTrackNumber: number,
    snapOverlapped: boolean = false,
  ) {
    if (elements.length === 0) return;
    console.log('setElementsTimelinePositions', elements);
    if (elements.length > 1) {
      console.log('updateMultiTrackElementsPositions');
      this.updateMultiTrackElementsPositions(elements, timeOffset);
      return;
    }

    const sortedElements = [...elements].sort(
      (a, b) => a.globalTime - b.globalTime,
    );
    const existingOverride =
      this.timelineStore.overrideTimelineElementsState[
        sortedElements[0].source.id
      ];

    const movingElement = new TimelineElement(sortedElements[0], {
      globalTime: existingOverride?.globalTime,
      proposedGlobalTime: existingOverride?.proposedGlobalTime,
      yOffset: existingOverride?.yOffset,
      // should probably add track override here
    });
    const overlappedElement = this.getOverlappedElement(movingElement);

    if (
      overlappedElement &&
      this.videoCreatorStore.isOriginalVideoElement(sortedElements[0].source)
    ) {
      this.updateElementPositionWithSnapping(movingElement, timeOffset);
      // this.handleOverlappingElements(
      //   movingElement,
      //   overlappedElement,
      //   timeOffset,
      //   snapOverlapped,
      // );
    } else {
      this.updateElementPositionWithSnapping(
        movingElement,
        timeOffset,
        yOffset,
        targetTrackNumber,
      );
    }
  }

  // This does not support yOffset or track changes for multi-element moves
  public updateMultiTrackElementsPositions(
    elements: ElementState[],
    offset: number,
  ) {
    runInAction(() => {
      elements.forEach(element => {
        const newGlobalTime =
          (this.timelineStore.overrideTimelineElementsState[element.source.id]
            ?.globalTime ?? element.globalTime) + offset;
        this.timelineStore.applyTimelinePlacementOverrideToElement(element, {
          globalTime: newGlobalTime,
          proposedGlobalTime: newGlobalTime,
        });
      });
    });
  }

  public getOverlappedElement(element: TimelineElement) {
    return this.elements.find(other => element.overlapsWith(other));
  }

  // As of now, this is only used for handling overlaps with original
  // video elements and original video element cannot change tracks.
  // Therefore, we do not need to pass targetTrackNumber or yOffset.
  public handleOverlappingElements(
    movingElement: TimelineElement,
    overlappedElement: TimelineElement,
    timeOffset: number,
    forceSnap: boolean,
  ) {
    const proposedStartTime = Math.max(
      0,
      movingElement.proposedGlobalTime + timeOffset,
    );
    const proposedEndTime = proposedStartTime + movingElement.duration;

    const shouldSwapWithPreviousElement =
      movingElement.headOverlapsWith(overlappedElement) &&
      proposedStartTime <= overlappedElement.overrideMidpointTime &&
      proposedStartTime >= overlappedElement.overrideStartTime;
    // proposedEndTime >= overlappedElement.overrideMidpointTime;

    const shouldSwapWithNextElement =
      movingElement.tailOverlapsWith(overlappedElement) &&
      proposedEndTime >= overlappedElement.overrideMidpointTime &&
      proposedEndTime <= overlappedElement.overrideEndTime;
    // proposedStartTime <= overlappedElement.overrideMidpointTime;

    const overlapsOnStart =
      proposedEndTime <= overlappedElement.overrideMidpointTime &&
      proposedEndTime >= overlappedElement.overrideStartTime;
    const overlapsOnEnd =
      proposedEndTime >= overlappedElement.overrideMidpointTime &&
      proposedStartTime <= overlappedElement.overrideEndTime;

    if (shouldSwapWithPreviousElement) {
      const anchorTime = overlappedElement.overrideStartTime;

      this.timelineStore.applyTimelinePlacementOverrideToElement(
        movingElement.element,
        { globalTime: anchorTime, proposedGlobalTime: proposedStartTime },
      );
      this.timelineStore.applyTimelinePlacementOverrideToElement(
        overlappedElement.element,
        { globalTime: anchorTime + movingElement.duration },
      );
      this.timelineStore.setElementsWithPlacementOverridesHaveDifferentOffsets(
        true,
      );
      this.videoStore.setSnapGuide(true, anchorTime);
    } else if (shouldSwapWithNextElement) {
      const anchorTime =
        overlappedElement.overrideStartTime - movingElement.duration;

      this.timelineStore.applyTimelinePlacementOverrideToElement(
        movingElement.element,
        {
          globalTime: anchorTime + overlappedElement.duration,
          proposedGlobalTime: proposedStartTime,
        },
      );
      this.timelineStore.applyTimelinePlacementOverrideToElement(
        overlappedElement.element,
        { globalTime: anchorTime },
      );
      this.timelineStore.setElementsWithPlacementOverridesHaveDifferentOffsets(
        true,
      );
      this.videoStore.setSnapGuide(
        true,
        anchorTime + overlappedElement.duration + movingElement.duration,
      );
    } else {
      let forceSnapBoundary: ElementBoundary | undefined;
      if (overlapsOnStart) {
        forceSnapBoundary = {
          time: overlappedElement.overrideStartTime - movingElement.duration,
          type: 'start',
          elementId: movingElement.sourceId,
        };
      } else if (overlapsOnEnd) {
        forceSnapBoundary = {
          time: overlappedElement.overrideEndTime + movingElement.duration,
          type: 'end',
          elementId: movingElement.sourceId,
        };
      }
      this.updateElementPositionWithSnapping(
        movingElement,
        timeOffset,
        forceSnapBoundary,
        forceSnap,
      );
    }
  }

  public updateElementPositionWithSnapping(
    element: TimelineElement,
    timeOffset: number,
    yOffset: number,
    targetTrackNumber: number,
    forceSnapWarningTo?: ElementBoundary,
    forceSnap?: boolean,
  ) {
    // this will probably need to generate boundaries from elements in the timeline store with
    // intermittent overrides applied
    const snapBoundaries = forceSnapWarningTo
      ? [forceSnapWarningTo]
      : this.videoStore.createSnapBoundariesForDrag([element.sourceId]);
    const newProposedGlobalTime = Math.max(
      0,
      element.proposedGlobalTime + timeOffset,
    );
    const newYOffset = element.yOffset + yOffset;

    const snapResult = getSnapResultForElement(
      newProposedGlobalTime,
      element.element,
      snapBoundaries,
      generateSnapThreshold(this.videoStore.timelineScale),
    );

    if (snapResult.isWithinSnapWarningRange) {
      this.videoStore.setSnapGuide(
        true,
        snapResult.snapGuideTime,
        snapResult.boundary ?? undefined,
      );
    } else if (forceSnapWarningTo) {
      this.videoStore.setSnapGuide(
        true,
        forceSnapWarningTo.time,
        forceSnapWarningTo,
      );
    } else {
      this.videoStore.setSnapGuide(false);
    }

    let newGlobalTime;
    if (snapResult.isWithinSnapRange) {
      newGlobalTime = snapResult.snapTime!;
    } else if (forceSnapWarningTo && forceSnap) {
      newGlobalTime =
        forceSnapWarningTo.time -
        (forceSnapWarningTo.type === 'end' ? element.duration : 0);
    } else {
      newGlobalTime = element.proposedGlobalTime;
    }
    // just to be safe
    newGlobalTime = Math.max(0, newGlobalTime);

    if (!this.videoStore.activeElementIds.includes(element.sourceId)) {
      // do we need this?
      this.videoStore.setActiveElements(element.sourceId);
    }

    // Track whether this operation snapped, so applyPlacements can decide on frame snapping
    this.timelineStore.setLastDragOperationSnapped(
      snapResult.isWithinSnapRange,
    );

    this.timelineStore.applyTimelinePlacementOverrideToElement(
      element.element,
      {
        globalTime: newGlobalTime,
        proposedGlobalTime: newProposedGlobalTime,
        yOffset: newYOffset,
        track: targetTrackNumber,
      },
    );
  }
}

class TimelineElement {
  constructor(
    public element: ElementState,
    public override: ElementPlacementOverride,
  ) {}

  get duration(): number {
    return this.element.duration;
  }

  get originalStartTime(): number {
    return this.element.globalTime;
  }

  get originalEndTime(): number {
    return this.originalStartTime + this.duration;
  }

  get originalMidpointTime(): number {
    return this.originalStartTime + this.duration / 2;
  }

  get overrideStartTime(): number {
    return this.override?.globalTime ?? this.originalStartTime;
  }

  get overrideEndTime(): number {
    return this.overrideStartTime + this.duration;
  }

  get overrideMidpointTime(): number {
    return this.overrideStartTime + this.duration / 2;
  }

  get proposedGlobalTime(): number {
    return this.override?.proposedGlobalTime ?? this.overrideStartTime;
  }

  get yOffset(): number {
    return this.override?.yOffset ?? 0;
  }

  get track(): number {
    return this.element.track;
  }

  get sourceId(): string {
    return this.element.source.id;
  }

  public headOverlapsWith(other: TimelineElement): boolean {
    return (
      this.sourceId !== other.sourceId &&
      this.track === other.track &&
      this.overrideStartTime < other.overrideEndTime &&
      this.overrideMidpointTime >= other.overrideMidpointTime
    );
  }

  public tailOverlapsWith(other: TimelineElement): boolean {
    return (
      this.sourceId !== other.sourceId &&
      this.track === other.track &&
      other.overrideStartTime < this.overrideEndTime &&
      other.overrideMidpointTime >= this.overrideMidpointTime
    );
  }

  public overlapsWith(other: TimelineElement): boolean {
    return this.headOverlapsWith(other) || this.tailOverlapsWith(other);
  }
}
